import React, { useCallback, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { IdentifyingInfoStep } from "./components/IdentifyingInfoStep";
import { GatherHouseholdDataStep } from "./components/GatherHouseholdDataStep";
import { IncomeGuidelinesStep } from "./components/IncomeGuidelinesStep";
import { AppointmentBookingStep } from "./components/AppointmentBookingStep";
import { ConfirmationStep } from "./components/ConfirmationStep";
import { EligibilityAlerts } from "./components/EligibilityAlerts";
import type { Step } from "./publicBookingFlowTypes";
import { ZipEntryStep } from "./components/ZipEntryStep";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { useReleaseSlotReservationMutation } from "./publicBookingFlowApi";
import { ReservationTimer } from "./components/ReservationTimer";
import { useCountdown } from "@/hooks/useCountdown";
import { reachedTerminal } from "./publicBookingFlowSlice";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetPublicBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi";
import { bookingSeasonGate } from "./bookingSeasonGate";
import { BookingSeasonClosedNotice } from "./components/BookingSeasonClosedNotice";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { IconAlertCircle } from "@tabler/icons-react";

const STEP_LABELS: Record<Step, string> = {
  1: "Find Site",
  2: "Personal",
  3: "Household",
  4: "Guidelines",
  5: "Scheduling",
  6: "Confirmation",
};

const TOTAL_STEPS = 6;

// The last step. Reaching it means the booking is already made, so the progress bar and the
// reservation countdown both stop here.
const CONFIRMATION_STEP = 6;

export const BookingFlowPage = () => {
  const currentStep = useAppSelector(state => state.publicBookingFlow.currentStep);
  const terminal = useAppSelector(state => state.publicBookingFlow.terminal);
  const targetSlot = useAppSelector(state => state.publicBookingFlow.targetSlot);
  const confirmedBooking = useAppSelector(state => state.publicBookingFlow.confirmedBooking);

  // Re-read on every visit rather than trusting a status cached from /booking a minute ago: an admin
  // can close booking at any moment. See bookingSeasonGate for what each answer — and a failed
  // read — renders as.
  const seasonQuery = useGetPublicBookingSeasonQuery(undefined, { refetchOnMountOrArgChange: true });
  const season = bookingSeasonGate(seasonQuery);

  // The confirmation screen is about a booking that is already made, so the season has no say over
  // it. Every other step waits for the season to be known and open — step 1 above all, which is
  // where a booking starts.
  const seasonBlocksSteps = season.state !== "open" && currentStep !== CONFIRMATION_STEP;

  const dispatch = useAppDispatch();
  const msRemaining = useCountdown(targetSlot?.reservationUntil);

  const [releaseSlotReservation] = useReleaseSlotReservationMutation();
  const releasedReservationUidRef = useRef<string | null>(null);
  const targetSlotRef = useRef(targetSlot);
  targetSlotRef.current = targetSlot;

  const releaseReservation = useCallback((reservationUid: string) => {
    if (releasedReservationUidRef.current === reservationUid) {
      return;
    }
    releasedReservationUidRef.current = reservationUid;
    // Best-effort: free the held slot rather than waiting for it to expire on its own.
    releaseSlotReservation(reservationUid).catch(() => {});
  }, [releaseSlotReservation]);

  // Release the held slot once the flow reaches a terminal state without a confirmed booking, or once
  // a booking is confirmed (the hold is no longer needed either way).
  useEffect(() => {
    if (targetSlot?.reservationUid && (terminal || confirmedBooking)) {
      releaseReservation(targetSlot.reservationUid);
    }
  }, [terminal, confirmedBooking, targetSlot, releaseReservation]);

  // The hold Cal granted has run out, so send the applicant somewhere that explains that rather than
  // letting them keep filling in a form against a slot they no longer have. Setting a terminal state
  // is enough to release the reservation too — the effect above already does that.
  //
  // Deliberately confined to the steps before booking — the data-entry steps plus the display-only
  // guidelines step, which sits inside the hold and takes no action of its own. Step 5 fires
  // createBooking on mount, and a client clock crossing zero mid-request must not contradict a
  // booking the server may have just created; there the timer stays visible but the server stays
  // authoritative, surfacing a genuinely lapsed hold as the 422 it already handles.
  useEffect(() => {
    if (msRemaining === 0 && currentStep >= 2 && currentStep <= 4 && !terminal && !confirmedBooking) {
      dispatch(reachedTerminal({ type: "reservation_expired" }));
    }
  }, [msRemaining, currentStep, terminal, confirmedBooking, dispatch]);

  // Safety net for in-app navigation away from the flow entirely while a slot is still held.
  useEffect(() => {
    return () => {
      const slot = targetSlotRef.current;
      if (slot?.reservationUid) {
        releaseReservation(slot.reservationUid);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = () => {
    if (terminal) {
      return <EligibilityAlerts />;
    }

    if (seasonBlocksSteps) {
      // No calThrottled branch: the season endpoint only reads the database and never calls Cal, so a
      // throttled 503 cannot come from it, and any failure gets the generic manual retry below.
      if (season.state === "unavailable") {
        return (
          <div className="w-full max-w-xl mx-auto space-y-4">
            <Alert>
              <IconAlertCircle />
              <AlertTitle>We couldn't load the appointment scheduler</AlertTitle>
              <AlertDescription>Please check your connection and try again.</AlertDescription>
            </Alert>
            <Button type="button" onClick={() => seasonQuery.refetch()} disabled={seasonQuery.isFetching}>
              Try again
            </Button>
          </div>
        );
      }
      return season.state === "closed"
        ? <BookingSeasonClosedNotice message={season.message} />
        : (
          <div className="w-full max-w-xl mx-auto space-y-4" aria-busy="true">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        );
    }

    switch (currentStep) {
      case 1:
        return (
          <ZipEntryStep />
        );
      case 2:
        return (
          <IdentifyingInfoStep />
        );
      case 3:
        return (
          <GatherHouseholdDataStep />
        );
      case 4:
        return (
          <IncomeGuidelinesStep />
        );
      case 5:
        return (
          <AppointmentBookingStep />
        );
      case 6:
        return <ConfirmationStep />;
      default:
        return null;
    }
  };

  const showProgressBar = !terminal && !seasonBlocksSteps && currentStep < CONFIRMATION_STEP;
  const progressPhase = Math.min(currentStep, TOTAL_STEPS);

  // Step 1 has no reservation yet and the confirmation step has already consumed it, so the
  // countdown is only meaningful in between.
  const showTimer = !terminal && !seasonBlocksSteps && !confirmedBooking && currentStep >= 2 && currentStep < CONFIRMATION_STEP && msRemaining !== null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex flex-col pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Progress bar */}
        {showProgressBar && (
          <div className="w-full max-w-xl mx-auto mb-8">
            <div className="flex items-center justify-between text-xs font-medium text-slate-400">
              {Object.entries(STEP_LABELS).map(([key, label]) => (
                <span key={key} style={progressPhase >= Number(key) ? { color: "#009681" } : undefined}>
                  {label}
                </span>
              ))}
            </div>
            <div className="w-full bg-slate-200 h-1.5 mt-2 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(progressPhase / TOTAL_STEPS) * 100}%`, background: "#009681" }}
              />
            </div>
          </div>
        )}

        {/* Reservation countdown */}
        {showTimer && <ReservationTimer msRemaining={msRemaining} />}

        {/* Step content */}
        <div className="flex-1 flex items-start justify-center">{renderContent()}</div>
      </main>

      <PublicFooter>
        Multi-Service Center &nbsp;·&nbsp; Energy Assistance
      </PublicFooter>
    </div>
  );
}
